<template>
    <div>
        <v-btn icon small @click="showDialog = true">
            <v-icon>mdi-close</v-icon>
        </v-btn>

        <CustomDialog
            :value="showDialog"
            :isInPopup="true"
            :defaultTitle="$t('checkout.refund.title')"
            @cancel="handleCancel"
            @input="showDialog = $event"
        >
            <template #content>
                <div class="refund-dialog-content ppv2-px-4">
                    <div v-if="error" class="refund-error ppv2-mb-6">
                        <v-icon color="error" small class="mr-1">mdi-alert-circle</v-icon>
                        {{ error }}
                    </div>

                    <div class="ppv2-mb-8 ppv2-text-center">
                        <label class="ppv2-block ppv2-text-base poppinsSemiBold ppv2-text-slate-800 ppv2-mb-4">
                            {{ $t('checkout.refund.amount_to_refund') }}
                        </label>
                        <v-form v-model="valid" class="ppv2-max-w-[300px] ppv2-mx-auto">
                            <v-text-field
                                outlined
                                dense
                                v-model="amount"
                                :disabled="loading"
                                :rules="[
                                    rules.required,
                                    rules.float,
                                    (value) => {
                                        const enteredValue = parseFloat(value)
                                        const maxAmount = parseFloat(this.item.amount)
                                        return (
                                            enteredValue <= maxAmount ||
                                            this.$t('checkout.refund.max_amount_error', { amount: maxAmount.toFixed(2) })
                                        )
                                    },
                                ]"
                                type="number"
                                step="any"
                                :suffix="$symbolCurrency"
                                class="refund-amount-input poppinsBold ppv2-text-lg"
                                :max="item.amount"
                                hide-details="auto"
                                flat
                                background-color="#F1F5F9"
                                height="48"
                            ></v-text-field>
                        </v-form>
                    </div>

                    <div class="ppv2-flex ppv2-justify-center ppv2-mb-2">
                        <div
                            class="ppv2-inline-flex ppv2-items-center ppv2-px-5 ppv2-py-2.5 ppv2-bg-slate-50 ppv2-rounded-2xl ppv2-border ppv2-border-slate-100"
                        >
                            <span class="poppinsMedium ppv2-text-slate-500 ppv2-text-sm mr-2"
                                >{{ $t('checkout.refund.initial_amount') }}
                            </span>
                            <span class="poppinsBold ppv2-text-slate-900 ppv2-text-base">
                                {{ parseFloat(item.amount).toFixed(2) }}{{ $symbolCurrency }}
                            </span>
                        </div>
                    </div>
                </div>
            </template>

            <template #actions>
                <BaseButton @click="handleCancel" customClass="!ppv2-min-h-[40px]" variant="outline" :disabled="loading">
                    {{ $t('checkout.refund.cancel') }}
                </BaseButton>
                <BaseButton
                    @click="refound"
                    customClass="!ppv2-min-h-[40px]"
                    variant="primary"
                    :disabled="loading || !valid"
                    :loading="loading"
                >
                    <span v-if="loading" class="d-flex align-center">
                        <v-progress-circular indeterminate size="16" width="2" color="white" class="mr-2"></v-progress-circular>
                        {{ $t('checkout.refund.processing') }}
                    </span>
                    <span v-else>{{ $t('checkout.refund.submit') }}</span>
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import axios from 'axios'
import CustomDialog from '../../Shared/CustomDialog.vue'
import BaseButton from '../../Shared/BaseButton.vue'

export default {
    components: { CustomDialog, BaseButton },
    props: ['userId', 'orderId', 'item'],
    mounted() {
        this.amount = this.item.amount
    },
    data: () => {
        return {
            amount: 0,
            showDialog: false,
            valid: false,
            loading: false,
            error: null,
            rules: {
                required: (value) => !!value || this.$t('checkout.refund.required'),
                number: (value) => {
                    const pattern = /^[0-9]*$/
                    return pattern.test(value) || this.$t('checkout.refund.wrong_format')
                },
                float: (value) => {
                    const pattern = /^[0-9]*([.,][0-9]+)?$/
                    return pattern.test(value) || this.$t('checkout.refund.wrong_float_format')
                },
            },
        }
    },
    watch: {
        showDialog(val) {
            if (val) {
                // Reset state when dialog opens
                this.error = null
                this.loading = false
                this.amount = this.item.amount
            }
        },
    },
    methods: {
        handleCancel() {
            if (!this.loading) {
                this.showDialog = false
            }
        },
        insertAfterEveryN(str, char, n, res) {
            const result = str.split('').reduce((accumulator, current, index) => {
                if (index % n === res) {
                    return accumulator + current + char
                }

                return accumulator + current
            }, '')

            return result
        },
        async refound() {
            if (this.loading) return // Prevent double-click

            this.loading = true
            this.error = null
            axios
                .post(`/back-office/caisse/refoundOrderInvoice`, { id: this.item.id, amount: this.amount })
                .then(() => {
                    window.location.reload()
                })
                .catch((error) => {
                    this.error = error?.response?.data?.message || this.$t('checkout.refund.error_message')
                })
                .finally(() => {
                    this.loading = false
                })
        },
    },
}
</script>

<style scoped>
.refund-dialog-content {
    padding: 8px 0;
}

.refund-error {
    background-color: #ffebee;
    color: #c62828;
    padding: 10px 12px;
    border-radius: 8px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    font-size: clamp(12px, 0.729545vw, 16px);
}

.refund-amount-input {
    margin-top: 8px;
}

.refund-info {
    font-size: 13px;
    color: #64748b;
}

.text-muted {
    color: #64748b;
}
</style>
